// Раннее обнаружение проблем: сводка bot_health_metrics за 24ч.
// Чтобы я видел rate-limit/403/429/неавторизованный webhook ДО того, как Telegram
// заморозит аккаунт. Источник — GET /api/health/summary.

import { useQuery } from '@tanstack/react-query';
import type { ComponentType } from 'react';
import { api } from '../api/client';
import type { HealthEvent, HealthEventType, HealthSummary } from '../api/types';
import { ScreenHeader } from '../components/ScreenHeader';
import {
  AlertTriangleIcon,
  BanIcon,
  type IconProps,
  LockIcon,
  RefreshIcon,
  TimerIcon,
  UserIcon,
} from '../components/icons';
import { humanError } from '../lib/errors';
import { haptic } from '../lib/telegram';
import { useBackButton } from '../lib/use-main-button';

type Props = {
  onBack: () => void;
};

type Tone = 'bad' | 'warn' | 'info' | 'quiet';

/**
 * Цвет строки-счётчика зависит от того, что СЕЙЧАС, а не от того, чем событие
 * могло бы быть.
 *
 * Раньше тон брался из типа события, и при пустых сутках экран показывал пять
 * красно-оранжевых плашек подряд — тревожный вид при полном порядке. Цвет,
 * который горит всегда, перестаёт что-либо сообщать, и настоящую тревогу потом
 * не видно. Теперь: ноль — нейтрально, было хоть раз — тон типа, порог пройден
 * — красный.
 */
function countTone(count: number, threshold: number, typeTone: Tone): Tone {
  if (count === 0) return 'quiet';
  if (count >= threshold) return 'bad';
  return typeTone;
}

const TYPE_META: Record<
  HealthEventType,
  { label: string; Icon: ComponentType<IconProps>; tone: Tone }
> = {
  rate_limit_hit: { label: 'Rate-limit', Icon: TimerIcon, tone: 'warn' },
  forbidden_403: { label: '403 Forbidden', Icon: BanIcon, tone: 'bad' },
  unknown_user: { label: 'Чужой user_id', Icon: UserIcon, tone: 'warn' },
  api_error: { label: 'API error', Icon: AlertTriangleIcon, tone: 'bad' },
  unauthorized_webhook: { label: 'Webhook 401', Icon: LockIcon, tone: 'bad' },
};

// Простые пороги «всё хорошо / тревога». Кастомизируется при появлении опыта.
const ALERT_THRESHOLDS: Record<HealthEventType, number> = {
  rate_limit_hit: 50,
  forbidden_403: 5,
  unknown_user: 20,
  api_error: 50,
  unauthorized_webhook: 10,
};

export function Health({ onBack }: Props): JSX.Element {
  useBackButton(onBack);

  const summaryQ = useQuery({
    queryKey: ['health-summary'],
    queryFn: () => api.healthSummary(),
    refetchInterval: 30_000,
  });

  return (
    <div className="screen">
      <ScreenHeader
        title="Диагностика"
        onBack={onBack}
        right={
          <button
            type="button"
            className="icon-btn"
            aria-label="Обновить"
            onClick={() => {
              haptic('light');
              summaryQ.refetch();
            }}
          >
            <RefreshIcon size={18} />
          </button>
        }
      />

      {summaryQ.isLoading ? (
        <div className="skeleton" style={{ height: 220 }} />
      ) : summaryQ.error ? (
        <div className="error">{humanError(summaryQ.error, 'Сводка не загрузилась')}</div>
      ) : summaryQ.data ? (
        <HealthBody data={summaryQ.data} />
      ) : null}
    </div>
  );
}

function HealthBody({ data }: { data: HealthSummary }): JSX.Element {
  const types = Object.keys(TYPE_META) as HealthEventType[];
  const anyAlerts = types.some((t) => (data.counts_by_type[t] ?? 0) >= ALERT_THRESHOLDS[t]);

  return (
    <>
      <div className="screen-note">
        За последние {data.window_hours}ч · всего событий: <b>{data.total}</b>
      </div>

      {data.total === 0 ? (
        <div className="empty-mini">
          За сутки — ни одного события. Это хорошо: бот работает, аномалий нет.
        </div>
      ) : null}

      {anyAlerts ? (
        <div className="health-alert">
          <AlertTriangleIcon size={18} />
          <span>Превышен порог по одному из типов событий — проверьте ниже.</span>
        </div>
      ) : null}

      <div className="health-list">
        {types.map((t) => (
          <CountRow
            key={t}
            type={t}
            count={data.counts_by_type[t] ?? 0}
            threshold={ALERT_THRESHOLDS[t]}
          />
        ))}
      </div>

      {/* Заголовок и «событий нет» показываем, только когда события бывают:
          при нулевых сутках это был бы второй пустой блок про то же самое,
          о чём уже сказано наверху. */}
      {data.total > 0 ? (
        <>
          <div className="section-header" style={{ marginTop: 16 }}>
            <div className="section-title-2">Последние события</div>
          </div>
          {data.recent.length === 0 ? (
            <div className="empty-mini">Событий нет</div>
          ) : (
            <div className="health-list">
              {data.recent.map((e) => (
                <EventRow key={e.id} event={e} />
              ))}
            </div>
          )}
        </>
      ) : null}
    </>
  );
}

function CountRow({
  type,
  count,
  threshold,
}: {
  type: HealthEventType;
  count: number;
  threshold: number;
}): JSX.Element {
  const meta = TYPE_META[type];
  const over = count >= threshold;
  const tone = countTone(count, threshold, meta.tone);
  return (
    <div className="health-count-row">
      <div className={`health-icon tone-${tone}`}>
        <meta.Icon size={18} />
      </div>
      <div className="health-main">
        <div className="health-name">{meta.label}</div>
      </div>
      <div className={`health-count ${over ? 'over' : ''}`}>
        {count} <span className="health-threshold">/ {threshold}</span>
      </div>
    </div>
  );
}

function EventRow({ event }: { event: HealthEvent }): JSX.Element {
  const meta = TYPE_META[event.event_type];
  const dt = new Date(event.timestamp);
  const time = `${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
  const day = `${pad(dt.getDate())}.${pad(dt.getMonth() + 1)}`;
  const summary = describePayload(event);
  return (
    <div className="health-event-row">
      <div className={`health-icon tone-${meta.tone}`}>
        <meta.Icon size={18} />
      </div>
      <div className="health-main">
        <div className="health-name">
          {meta.label}
          {event.error_code ? ` · ${event.error_code}` : ''}
        </div>
        <div className="health-meta">
          {day} {time}
          {summary ? ` · ${summary}` : ''}
        </div>
      </div>
    </div>
  );
}

function describePayload(e: HealthEvent): string {
  const p = e.payload ?? {};
  const parts: string[] = [];
  if (e.user_id != null) parts.push(`uid ${e.user_id}`);
  const source = typeof p.source === 'string' ? p.source : null;
  if (source) parts.push(source);
  const method = typeof p.method === 'string' ? p.method : null;
  if (method) parts.push(method);
  const retry =
    (p as { retry_after?: unknown; retry_after_ms?: unknown }).retry_after ??
    (p as { retry_after_ms?: unknown }).retry_after_ms;
  if (typeof retry === 'number') parts.push(`retry ${retry}`);
  return parts.join(' · ');
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
